import type { DownloadableFile, Base64 } from '../types'

const base64DataToBlob = (data: Base64, mimeType: string): Blob => {
  let byteArray: Uint8Array

  if ('fromBase64' in Uint8Array) {
    // use faster native function when available
    byteArray = (Uint8Array.fromBase64 as (s: string) => Uint8Array)(data)
  } else {
    const decoded = atob(data)
    byteArray = new Uint8Array(decoded.length)

    for (let i = 0; i < decoded.length; ++i) {
      byteArray[i] = decoded.charCodeAt(i)
    }
  }

  const arrayBuffer = new ArrayBuffer(byteArray.byteLength)
  new Uint8Array(arrayBuffer).set(byteArray)

  return new Blob([arrayBuffer], { type: mimeType })
}

export const toBlob = (file: DownloadableFile): Blob => {
  return file.content instanceof Blob
    ? file.content
    : base64DataToBlob(file.content, file.mimeType ?? 'application/octet-stream')
}

const toBlobUrl = (file: DownloadableFile): string => {
  return URL.createObjectURL(toBlob(file))
}

const download = (file: DownloadableFile, blobUrl: string): void => {
  try {
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = file.name
    link.click()
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 0)
  }
}

const preview = (blobUrl: string): boolean => {
  const fileWindow = window.open(blobUrl, '_blank')

  if (!fileWindow) {
    return false
  }

  fileWindow.opener = null
  fileWindow.addEventListener('unload', () => URL.revokeObjectURL(blobUrl))

  return true
}

export async function downloadFile(file: DownloadableFile): Promise<void> {
  download(file, toBlobUrl(file))
}

/** @deprecated Serve the file from the server via openPostForm instead. */
export async function previewFile(file: DownloadableFile): Promise<void> {
  const blobUrl = toBlobUrl(file)

  try {
    if (!preview(blobUrl)) {
      download(file, blobUrl)
    }
  } catch (e) {
    download(file, blobUrl)
  }
}

const BYTES_IN_MB = 1024 * 1024

export function formatFileSize(bytes: number): string {
  const mb = bytes / BYTES_IN_MB

  if (mb >= 1) {
    return `${mb.toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`
  }

  return `${Math.max(1, Math.round(bytes / 1024))} kB`
}

export function isFileSizeExceeded(bytes: number, maxSizeMb: number): boolean {
  return bytes > maxSizeMb * BYTES_IN_MB
}
